import { pipe } from 'ramda';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { Just, Maybe, Nothing } from 'core/maybe';
import { on, Result } from 'core/result';
import { unreachable } from 'utils/unreachable';
import { formatter } from './formatter';
import { Parser } from './parser';

type InitialState = Maybe<number>;
type Editing<E> = ['editing', string, Result<number, E>, InitialState];
type State<E> = InitialState | Editing<E>;

const stringValue = <E>(state: State<E>): string | undefined => {
  switch (state[0]) {
    case 'just':
      return formatter(state[1]);
    case 'editing':
      return state[1];
    default:
      return undefined;
  }
};

export const getMinutes = <E>(state: State<E>): Maybe<number> => {
  switch (state[0]) {
    case 'just':
    case 'nothing':
      return state;
    case 'editing':
      return state[3];
    default:
      return Nothing();
  }
};

export const getError = <E>(state: State<E>): Maybe<E> => {
  switch (state[0]) {
    case 'just':
    case 'nothing':
      return Nothing();
    case 'editing':
      switch (state[2][0]) {
        case 'ok':
          return Nothing();
        case 'error':
          return Just(state[2][1]);
        default:
          return Nothing();
      }
    default:
      return Nothing();
  }
};

export const useInputDuration = <E>(
  minutes: Maybe<number> = Nothing(),
  parser: Parser<number, E>,
) => {
  const [state, setState] = useState<State<E>>(minutes);
  const justMinutesProps = getMinutes(minutes);
  const valueMinutesProps =
    justMinutesProps[0] === 'just' ? justMinutesProps[1] : 0;

  const _minutesState = minutes[0];

  useEffect(() => {
    setState((oldValue) => {
      const minutesInUI = getMinutes(oldValue);
      const valueMinutesState = minutesInUI[0] === 'just' ? minutesInUI[1] : 0;

      if (valueMinutesState === valueMinutesProps) {
        return oldValue;
      }
      if (_minutesState === 'nothing') {
        return oldValue;
      }

      return valueMinutesProps === 0 ? Nothing() : Just(valueMinutesProps);
    });
  }, [valueMinutesProps, _minutesState]);
  const handleChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) =>
      pipe(
        () => ev.target.value ?? null,
        (x) => [x, parser(x)] as [string, Result<number, E>],
        ([x, result]) =>
          setState([
            'editing',
            x,
            result,
            state[0] === 'editing' ? state[3] : state,
          ]),
      )(),
    [state, setState, parser],
  );
  const handlePressEnter = useCallback(
    (_: KeyboardEvent<HTMLInputElement>) => {
      switch (state[0]) {
        case 'just':
        case 'nothing':
          break;
        case 'editing':
          pipe(
            () => state[2],
            on(
              (m) => setState(Just(m)),
              () => setState(state[3]),
            ),
          )();
          break;
        default:
          unreachable(state);
      }
    },
    [state, setState],
  );
  const handleBlur = useCallback(
    (_: FocusEvent<HTMLInputElement>) => {
      switch (state[0]) {
        case 'just':
        case 'nothing':
          break;
        case 'editing':
          pipe(
            () => state[2],
            on(
              (m) => setState(Just(m)),
              () => setState(state[3]),
            ),
          )();
          break;
        default:
          unreachable(state);
      }
    },
    [state, setState],
  );

  return {
    value: stringValue(state),
    handleChange,
    handlePressEnter,
    handleBlur,
    state,
  };
};
